import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, MapPin, MapPinOff } from 'lucide-react';
import { motion } from 'framer-motion';
import { calculateDistance, formatDistance } from '../utils/distance';
import { useMusicTranslations } from '../utils/translations';
import type { Band } from '../types/database';

interface BandWithDistance extends Band {
  distance?: number;
}

interface BandPanelProps {
  band: BandWithDistance;
  nearMeEnabled?: boolean;
}

export function BandPanel({ band, nearMeEnabled = false }: BandPanelProps) {
  const { t } = useTranslation();
  const { translateGenre } = useMusicTranslations();
  
  return (
    <Link key={band.id} to={`/bands/${band.slug}`} className="block w-full h-full">
      <motion.div 
        className="panel hover:bg-gray-custom/20"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        whileHover={{ scale: 1.02 }}
      >
        <motion.div 
          className="flex flex-col items-center mb-3"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <motion.div 
            className="h-36 w-36 bg-gray-custom/10 flex items-center justify-center overflow-hidden mb-4"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
          >
            {band.avatar_url ? (
              <img
                src={band.avatar_url}
                alt={band.name}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <Users className="w-12 h-12 text-light/60" />
            )}
          </motion.div>
          <motion.h3 
            className="font-tilt-warp text-xl text-light text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {band.name}
          </motion.h3>
          <motion.div 
            className="flex items-center justify-between text-light/60 text-sm mt-2"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className="flex items-center">
              {band.location ? (
                <>
                  <MapPin className="w-4 h-4 mr-1" />
                  <span>{band.location}</span>
                </>
              ) : (
                <>
                  <MapPinOff className="w-4 h-4 mr-1" />
                  <span>{t('common.notSpecified')}</span>
                </>
              )}
            </div>
            {nearMeEnabled && band.distance !== undefined && (
              <span className="text-primary ml-2">
                {formatDistance(band.distance)}
              </span>
            )}
          </motion.div>
        </motion.div>
        {band.genres && band.genres.length > 0 && (
          <motion.div 
            className="flex flex-wrap justify-center gap-2"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            {band.genres.map((genre, index) => (
              <motion.span
                key={genre}
                className="px-2 py-1 bg-tertiary rounded-full text-sm text-white"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: 0.6 + index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                {translateGenre(genre)}
              </motion.span>
            ))}
            {band.status && band.status != "Aktiv" && (
              <motion.span
                className="px-2 py-1 bg-primary rounded-full text-sm text-white"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: 0.6 }}
                whileHover={{ scale: 1.05 }}
              >
                {band.status}
              </motion.span>
            )}
          </motion.div>
        )}
      </motion.div>
    </Link>
  );
}